import Cookies from 'js-cookie';
import { format } from 'date-fns';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { NavigateFunction } from 'react-router-dom';

import { PaginationControl } from 'shared/components/pagination';
import { AsyncState } from 'shared/libs/state';
import {
   Transaction,
   TransactionActionResponse,
   TransactionAdmin,
   TransactionsIndexResponse,
   TransactionSortDirection,
   TransactionSortKey,
} from './types';
import { TransactionsApiService } from './transactions.api-service';

const PER_PAGE = 20;
const ALL_ADMINS_VALUE = 'ALL';

const getCurrentDateFormatted = () => {
   const date = new Date();
   const day = String(date.getDate()).padStart(2, '0');
   const month = String(date.getMonth() + 1).padStart(2, '0');
   const year = date.getFullYear();

   return `${day}.${month}.${year}`;
};

const parseAppDate = (value: string | null) => {
   if (!value) {
      return 0;
   }

   const [day, month, year] = value.split('.').map(Number);
   if (!day || !month || !year) {
      return 0;
   }

   return new Date(year, month - 1, day).getTime();
};

export class TransactionsStore {
   readonly loadTransactionsState = new AsyncState<TransactionsIndexResponse>();
   readonly activateTransactionState = new AsyncState<TransactionActionResponse>();
   readonly deactivateTransactionState = new AsyncState<TransactionActionResponse>();
   readonly downloadTransactionsState = new AsyncState<Blob>();
   readonly pagination = new PaginationControl();

   rawTransactions: Transaction[] = [];
   admins: TransactionAdmin[] = [];
   searchStr = '';
   selectedAdminId = ALL_ADMINS_VALUE;
   dateFrom: Date | null = null;
   dateTo: Date | null = null;
   sorter: TransactionSortKey = 'created';
   direction: TransactionSortDirection = 'desc';
   selectedTransaction: Transaction | null = null;
   totalRecords = 0;
   totalPageCount = 0;
   toPage = '1';

   constructor(private readonly navigate: NavigateFunction) {
      makeObservable(this, {
         rawTransactions: observable,
         admins: observable,
         searchStr: observable,
         selectedAdminId: observable,
         dateFrom: observable,
         dateTo: observable,
         sorter: observable,
         direction: observable,
         selectedTransaction: observable,
         totalRecords: observable,
         totalPageCount: observable,
         toPage: observable,
         filteredTransactions: computed,
         transactions: computed,
         totalCount: computed,
         totalPages: computed,
         loading: computed,
         actionLoading: computed,
         recordStart: computed,
         recordEnd: computed,
         onSearchChange: action.bound,
         onAdminChange: action.bound,
         setDateFrom: action.bound,
         setDateTo: action.bound,
         submitSearch: action.bound,
         onSortBy: action.bound,
         openTransactionDialog: action.bound,
         closeTransactionDialog: action.bound,
         onToPageChange: action.bound,
         loadPage: action.bound,
         goToPage: action.bound,
      });
   }

   get filteredTransactions(): Transaction[] {
      return [...this.rawTransactions].sort((a, b) => this.compareTransactions(a, b));
   }

   get transactions(): Transaction[] {
      return this.filteredTransactions;
   }

   get totalCount(): number {
      return this.totalRecords;
   }

   get totalPages(): number {
      return this.totalPageCount;
   }

   get loading(): boolean {
      return this.loadTransactionsState.status === 'pending';
   }

   get actionLoading(): boolean {
      return (
         this.activateTransactionState.status === 'pending' ||
         this.deactivateTransactionState.status === 'pending'
      );
   }

   get recordStart(): number {
      return this.totalCount === 0 ? 0 : this.pagination.page * PER_PAGE + 1;
   }

   get recordEnd(): number {
      return Math.min(
         this.pagination.page * PER_PAGE + this.rawTransactions.length,
         this.totalCount
      );
   }

   onSearchChange(value: string): void {
      this.searchStr = value;
   }

   onAdminChange(value: string): void {
      this.selectedAdminId = value;
   }

   setDateFrom(dateFrom: Date | null): void {
      this.dateFrom = dateFrom;

      if (dateFrom && this.dateTo && dateFrom > this.dateTo) {
         this.dateTo = null;
      }
   }

   setDateTo(dateTo: Date | null): void {
      this.dateTo = dateTo;

      if (dateTo && this.dateFrom && dateTo < this.dateFrom) {
         this.dateFrom = null;
      }
   }

   async submitSearch(): Promise<void> {
      await this.loadPage(0);
   }

   async onSortBy(sort: TransactionSortKey): Promise<void> {
      if (this.sorter === sort) {
         this.direction = this.direction === 'asc' ? 'desc' : 'asc';
      } else {
         this.sorter = sort;
         this.direction = 'asc';
      }
      await this.loadPage(0);
   }

   openTransactionDialog(transaction: Transaction): void {
      this.selectedTransaction = transaction;
   }

   closeTransactionDialog(): void {
      this.selectedTransaction = null;
   }

   onToPageChange(value: string): void {
      if (/^\d+$/.test(value) || value === '') {
         this.toPage = value;
      }
   }

   async loadPage(page: number): Promise<void> {
      const safePage = this.getSafePage(page);
      this.pagination.setPage(safePage);
      this.toPage = String(safePage + 1);
      await this.loadTransactions(safePage);
   }

   async goToPage(): Promise<void> {
      if (this.totalPages === 0) {
         return;
      }

      if (this.toPage === '') {
         this.toPage = '1';
      }

      const page = Number(this.toPage);
      if (Number.isNaN(page)) {
         return;
      }

      const nextPage = Math.min(Math.max(page, 1), this.totalPages) - 1;
      await this.loadPage(nextPage);
   }

   async loadTransactions(page = this.pagination.page): Promise<void> {
      const tokenKey = Cookies.get('token');
      const adminID = Cookies.get('adminID');

      if (!tokenKey) {
         return this.navigate?.('/admin');
      }

      await this.loadTransactionsState.run(
         () =>
            TransactionsApiService.loadTransactions(
               {
                  page: Math.max(Math.floor(page), 0) + 1,
                  per_page: PER_PAGE,
                  search: this.searchStr.trim() || null,
                  admin_id:
                     this.selectedAdminId === ALL_ADMINS_VALUE
                        ? null
                        : Number(this.selectedAdminId),
                  date_from: this.dateFrom ? format(this.dateFrom, 'yyyy-MM-dd') : null,
                  date_to: this.dateTo ? format(this.dateTo, 'yyyy-MM-dd') : null,
               },
               {
                  adminID,
                  tokenKey,
               }
            ),
         {
            onSuccess: (resp) => {
               const responseList = Array.isArray(resp.response) ? resp.response : [];
               const responseAdmins = Array.isArray(resp.admins) ? resp.admins : [];
               const responseTotal = Number(resp.pagination?.total);
               const responseLastPage = Number(resp.pagination?.last_page);
               const responseCurrentPage = Number(resp.pagination?.current_page);
               const nextTotalRecords = Number.isFinite(responseTotal)
                  ? responseTotal
                  : responseList.length;
               const nextTotalPages = Number.isFinite(responseLastPage)
                  ? responseLastPage
                  : Math.ceil(nextTotalRecords / PER_PAGE);
               const nextPage =
                  Number.isFinite(responseCurrentPage) && responseCurrentPage > 0
                     ? responseCurrentPage - 1
                     : page;

               runInAction(() => {
                  this.rawTransactions = responseList;
                  this.admins = responseAdmins;
                  this.totalRecords = nextTotalRecords;
                  this.totalPageCount = nextTotalPages;
                  this.pagination.setPage(nextPage);
                  this.toPage = String(nextPage + 1);
               });
               this.ensurePageInRange();
            },
         }
      );
   }

   async activateTransaction({
      id,
      onSuccess,
      onError,
   }: {
      id: number;
      onSuccess?: (resp: TransactionActionResponse) => void;
      onError?: (message: string) => void;
   }): Promise<void> {
      await this.changeTransactionStatus({
         id,
         action: 'activate',
         onSuccess,
         onError,
      });
   }

   async deactivateTransaction({
      id,
      onSuccess,
      onError,
   }: {
      id: number;
      onSuccess?: (resp: TransactionActionResponse) => void;
      onError?: (message: string) => void;
   }): Promise<void> {
      await this.changeTransactionStatus({
         id,
         action: 'deactivate',
         onSuccess,
         onError,
      });
   }

   async downloadTransactions({
      onSuccess,
      onError,
   }: {
      onSuccess?: () => void;
      onError?: (message: string) => void;
   } = {}): Promise<void> {
      const tokenKey = Cookies.get('token');
      const adminID = Cookies.get('adminID');

      if (!tokenKey) {
         return this.navigate?.('/admin');
      }

      await this.downloadTransactionsState.run(
         () =>
            TransactionsApiService.downloadTransactions({
               adminID,
               tokenKey,
            }),
         {
            onSuccess: (blob) => {
               const url = window.URL.createObjectURL(blob);
               const link = document.createElement('a');
               link.href = url;
               link.setAttribute('download', `all_payments_${getCurrentDateFormatted()}.xlsx`);
               document.body.appendChild(link);
               link.click();
               document.body.removeChild(link);
               window.URL.revokeObjectURL(url);
               onSuccess?.();
            },
            onError: (error) => {
               onError?.(error.message);
            },
         }
      );
   }

   private async changeTransactionStatus({
      id,
      action,
      onSuccess,
      onError,
   }: {
      id: number;
      action: 'activate' | 'deactivate';
      onSuccess?: (resp: TransactionActionResponse) => void;
      onError?: (message: string) => void;
   }): Promise<void> {
      const tokenKey = Cookies.get('token');
      const adminID = Cookies.get('adminID');

      if (!tokenKey) {
         return this.navigate?.('/admin');
      }

      const state =
         action === 'activate' ? this.activateTransactionState : this.deactivateTransactionState;
      const request =
         action === 'activate'
            ? TransactionsApiService.activateTransaction
            : TransactionsApiService.deactivateTransaction;

      await state.run(
         () =>
            request(id, {
               adminID,
               tokenKey,
            }),
         {
            onSuccess: async (resp) => {
               if (resp.status === 200) {
                  await this.loadTransactions(this.pagination.page);
                  onSuccess?.(resp);
               } else {
                  onError?.(resp.message);
               }
            },
            onError: (error) => {
               onError?.(error.message);
            },
         }
      );
   }

   private compareTransactions(a: Transaction, b: Transaction): number {
      const left = this.getComparableValue(a);
      const right = this.getComparableValue(b);

      if (left < right) {
         return this.direction === 'asc' ? -1 : 1;
      }

      if (left > right) {
         return this.direction === 'asc' ? 1 : -1;
      }

      return 0;
   }

   private getComparableValue(transaction: Transaction): string | number {
      const value = transaction[this.sorter];

      if (this.sorter === 'created' || this.sorter === 'start_date' || this.sorter === 'end_date') {
         return parseAppDate(value as string | null);
      }

      if (typeof value === 'boolean') {
         return value ? 1 : 0;
      }

      if (typeof value === 'string') {
         return value.toLocaleLowerCase();
      }

      return value ?? '';
   }

   getAdminDisplayName(admin: TransactionAdmin): string {
      return admin.name || `${admin.first_name ?? ''} ${admin.last_name ?? ''}`.trim();
   }

   private getSafePage(page: number): number {
      if (!Number.isFinite(page) || page < 0) {
         return 0;
      }

      if (this.totalPages === 0) {
         return Math.floor(page);
      }

      return Math.min(Math.floor(page), this.totalPages - 1);
   }

   private ensurePageInRange(): void {
      const safePage = this.getSafePage(this.pagination.page);
      if (safePage !== this.pagination.page) {
         void this.loadPage(safePage);
      }
   }
}
